import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { getEntities } from 'app/modules/administration/bank/bank.reducer';
import { getEntities as getDepositEntities, updateStatus } from 'app/entities/deposit-transaction/deposit-transaction.reducer';
import { DepositCreatedDialog } from 'app/entities/deposit/deposit-created-dialog';
import { CryptoCreatedDialog } from 'app/entities/deposit/crypto-created-dialog';
import { convertDateTimeFromServer } from 'app/shared/util/date-utils';
import { IoReload } from 'react-icons/io5';
import axios from 'axios';
import { getAccount } from 'app/shared/reducers/authentication';
import { colors, formatVND } from 'app/shared/theme/colors';
import { useTranslation } from 'app/shared/i18n';
import { DepositRankWidget } from './deposit-rank-widget';

export const Deposit = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const bankList = useAppSelector(state => state.bank.entities);
  const [bankChoose, setBankChoose] = useState(null);
  const [amount, setAmount] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [method, setMethod] = useState<'bank' | 'crypto'>('bank');
  const [showCrypto, setShowCrypto] = useState(false);
  const [cryptoEnabled, setCryptoEnabled] = useState(false);

  useEffect(() => {
    dispatch(getEntities({ sort: `id,ASC` }));
  }, []);
  useEffect(() => {
    if (bankList.length > 0) setBankChoose(bankList[0]);
  }, [bankList]);
  // Chỉ hiện tab Crypto khi tính năng đang BẬT + có coin khả dụng.
  useEffect(() => {
    axios
      .get('api/crypto-deposit/assets')
      .then(res => setCryptoEnabled(!!res.data?.data?.enabled && (res.data?.data?.assets?.length || 0) > 0))
      .catch(() => setCryptoEnabled(false));
  }, []);

  const handleChange = e => {
    let v = e.target.value.replace(/\./g, '');
    if (/^\d*$/.test(v)) {
      v = v.replace(/^0+(?=\d)/, '');
      setAmount(v === '' ? '' : Number(v).toLocaleString('de-DE'));
    }
  };

  return (
    <div style={{ maxWidth: 880, margin: '0 auto', padding: '36px 24px' }}>
      <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
      <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 24, display: 'flex', alignItems: 'center', gap: 10 }}>
        {t('deposit.title')}
      </h2>

      {/* Rank widget */}
      <DepositRankWidget />

      {/* Chọn phương thức: Ngân hàng | Crypto — chỉ hiện khi crypto đang bật */}
      {cryptoEnabled && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {[
            { k: 'bank', label: `🏦 ${t('deposit.methodBank', { defaultValue: 'Ngân hàng' })}` },
            { k: 'crypto', label: `🪙 ${t('deposit.methodCrypto', { defaultValue: 'Crypto' })}` },
          ].map(m => (
            <div
              key={m.k}
              onClick={() => setMethod(m.k as 'bank' | 'crypto')}
              style={{
                flex: 1,
                textAlign: 'center',
                padding: '10px 12px',
                borderRadius: 10,
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: 14,
                border: `1px solid ${method === m.k ? colors.accent : colors.border}`,
                background: method === m.k ? colors.accentDim : colors.bgCard,
                color: method === m.k ? colors.accent : colors.muted,
              }}
            >
              {m.label}
            </div>
          ))}
        </div>
      )}

      {(method === 'bank' || !cryptoEnabled) &&
        (bankList.length === 0 ? (
          <div className="ms-card" style={{ padding: 40, textAlign: 'center' }}>
            <div style={{ fontSize: 40, opacity: 0.3, marginBottom: 10 }}>🏦</div>
            <div style={{ color: colors.dim }}>{t('deposit.bankMaintenance')}</div>
          </div>
        ) : (
          <>
            {/* Bank selection */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 20 }}>
              {bankList.map(bank => (
                <div
                  key={bank.id}
                  className="ms-card"
                  onClick={() => setBankChoose(bank)}
                  style={{
                    padding: 14,
                    cursor: 'pointer',
                    borderColor: bankChoose?.id === bank.id ? colors.accent : colors.border,
                    background: bankChoose?.id === bank.id ? colors.accentDim : colors.bgCard,
                    transition: 'all .15s',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {bank.logo && <img src={bank.logo} alt={bank.displayName} style={{ width: 32, height: 32, borderRadius: 6 }} />}
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: bankChoose?.id === bank.id ? colors.accent : colors.text }}>
                        {bank.displayName}
                      </div>
                      <div style={{ fontSize: 11, color: colors.dim, fontFamily: "'JetBrains Mono', monospace" }}>{bank.accountNumber}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Amount input */}
            <div className="ms-card" style={{ padding: 20, marginBottom: 20 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <label className="ms-label">{t('deposit.amountLabel')}</label>
                  <input
                    className="ms-input"
                    value={amount}
                    onChange={handleChange}
                    placeholder={t('deposit.amountPlaceholder')}
                    style={{ height: 48, fontSize: 20, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", textAlign: 'right' }}
                  />
                </div>
                <button className="ms-btn ms-btn--primary" onClick={() => setShowDialog(true)} style={{ height: 48, padding: '0 24px' }}>
                  {t('deposit.pay')}
                </button>
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                {[10000, 50000, 100000, 200000, 500000, 1000000].map(v => (
                  <button
                    key={v}
                    onClick={() => setAmount(Number(v).toLocaleString('de-DE'))}
                    style={{
                      flex: v === 10000 ? 0.7 : 1,
                      padding: 7,
                      borderRadius: 8,
                      fontSize: v === 10000 ? 10 : 11,
                      fontWeight: 500,
                      fontFamily: "'JetBrains Mono', monospace",
                      cursor: 'pointer',
                      background: amount === Number(v).toLocaleString('de-DE') ? colors.accentDim : colors.surface,
                      border: `1px solid ${amount === Number(v).toLocaleString('de-DE') ? colors.accent + '44' : colors.border}`,
                      color: amount === Number(v).toLocaleString('de-DE') ? colors.accent : colors.muted,
                    }}
                  >
                    {v / 1000}K
                  </button>
                ))}
              </div>
            </div>
          </>
        ))}

      {method === 'crypto' && cryptoEnabled && (
        <div className="ms-card" style={{ padding: 20, marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label className="ms-label">{t('deposit.amountLabel')}</label>
              <input
                className="ms-input"
                value={amount}
                onChange={handleChange}
                placeholder={t('deposit.amountPlaceholder')}
                style={{ height: 48, fontSize: 20, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", textAlign: 'right' }}
              />
            </div>
            <button className="ms-btn ms-btn--primary" onClick={() => setShowCrypto(true)} style={{ height: 48, padding: '0 24px' }}>
              {t('deposit.pay')}
            </button>
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
            {[10000, 50000, 100000, 200000, 500000, 1000000].map(v => (
              <button
                key={v}
                onClick={() => setAmount(Number(v).toLocaleString('de-DE'))}
                style={{
                  flex: v === 10000 ? 0.7 : 1,
                  padding: 7,
                  borderRadius: 8,
                  fontSize: v === 10000 ? 10 : 11,
                  fontWeight: 500,
                  fontFamily: "'JetBrains Mono', monospace",
                  cursor: 'pointer',
                  background: amount === Number(v).toLocaleString('de-DE') ? colors.accentDim : colors.surface,
                  border: `1px solid ${amount === Number(v).toLocaleString('de-DE') ? colors.accent + '44' : colors.border}`,
                  color: amount === Number(v).toLocaleString('de-DE') ? colors.accent : colors.muted,
                }}
              >
                {v / 1000}K
              </button>
            ))}
          </div>
          <div style={{ marginTop: 10, fontSize: 12, color: colors.dim }}>
            {t('deposit.cryptoHint', { defaultValue: 'Chọn mạng & coin ở bước sau.' })}
          </div>
        </div>
      )}

      <ListDeposit />

      <DepositCreatedDialog isOpen={showDialog} bankId={bankChoose?.id} amount={amount} handleClose={() => setShowDialog(false)} />
      <CryptoCreatedDialog isOpen={showCrypto} amount={amount} handleClose={() => setShowCrypto(false)} />
    </div>
  );
};

const ListDeposit = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const depositTransactionList = useAppSelector(state => state.depositTransaction.entities);

  useEffect(() => {
    dispatch(getDepositEntities({ page: 0, size: 10, sort: `createdDate,DESC` }));
  }, []);

  const checkStatusTable = async code => {
    const { data } = await axios.get(`api/deposit/check/${code}`);
    dispatch(updateStatus({ code, isApproved: data.data.isApproved, expired: data.data.expired }));
    if (data.data.isApproved) {
      dispatch(getAccount());
      dispatch(getDepositEntities({ sort: `createdDate,DESC` }));
    }
  };

  return (
    <div style={{ marginTop: 30 }}>
      <h2
        style={{ fontSize: 22, fontWeight: 700, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}
      >{`📋 ${t('deposit.depositHistory')}`}</h2>
      <div className="ms-card" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="ms-table" style={{ minWidth: 500 }}>
            <thead>
              <tr>
                <th>Code</th>
                <th style={{ textAlign: 'center' }}>{t('deposit.amount')}</th>
                <th style={{ textAlign: 'center' }}>{t('deposit.status')}</th>
                <th style={{ textAlign: 'center' }}>{t('deposit.note')}</th>
                <th style={{ textAlign: 'center' }}>{t('deposit.date')}</th>
              </tr>
            </thead>
            <tbody>
              {depositTransactionList.map(dep => (
                <tr key={dep.id}>
                  <td style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: colors.accent }}>{dep.code}</td>
                  <td style={{ textAlign: 'center', fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, color: colors.success }}>
                    {formatVND(dep.amount)}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {dep.isApproved ? (
                      <span className="ms-badge ms-badge--success">{t('deposit.success')}</span>
                    ) : dep.note ? (
                      <span className="ms-badge ms-badge--danger">{t('deposit.failed')}</span>
                    ) : dep.expired ? (
                      <span className="ms-badge ms-badge--danger">{t('deposit.expiredStatus', { defaultValue: 'Hết hạn' })}</span>
                    ) : (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span className="ms-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          <span
                            style={{
                              display: 'inline-block',
                              width: 12,
                              height: 12,
                              border: `2px solid ${colors.accent}33`,
                              borderTopColor: colors.accent,
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite',
                            }}
                          />
                          {t('deposit.processing')}
                        </span>
                        <IoReload
                          style={{ cursor: 'pointer', color: colors.accent }}
                          onClick={() => checkStatusTable(dep.code)}
                          title={t('deposit.checkStatus')}
                        />
                      </span>
                    )}
                  </td>
                  <td style={{ textAlign: 'center', fontSize: 12, color: colors.dim }}>
                    {dep.note === 'Nạp tiền thành công'
                      ? t('deposit.noteSuccess')
                      : dep.note === 'Nạp tiền thất bại'
                        ? t('deposit.noteFailed')
                        : dep.note}
                  </td>
                  <td style={{ textAlign: 'center', fontSize: 12, color: colors.dim }}>
                    {convertDateTimeFromServer(new Date(dep.createdDate))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
